import React, { useCallback, useState } from 'react'
import { NavLink } from 'react-router-dom'
import moment from 'moment'
import styled from 'styled-components'

import ArchiveDeviceModal from 'pages/shared/ArchivedDevices/ArchiveDeviceModal'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import BreadCrumbLink from 'components/BreadCrumbLink'
import Spinner from 'components/Spinner'
import Table from 'components/Table'

import { useStoreContext } from 'hooks/StoreContext'
import useUnassignedDevices from 'hooks/useUnassignedDevices'
import { useSessionContext } from 'hooks/SessionContext'

import Api from 'services/api'

import { PageTitle, screenSize, SinglePanePage } from 'styles/shared'

import { ArchivedDevice } from 'types/ArchivedDevice'

type ArchiveDeviceModalState = {
  [index: string]: boolean
}

const UnassignedDevices = () => {
  const { session: { dealerId } } = useSessionContext()
  const [refreshTimestamp, setRefreshTimestamp] = useState(moment().valueOf())
  const refreshCallback = useCallback(() => setRefreshTimestamp(moment().valueOf()), [])

  useUnassignedDevices(refreshTimestamp)

  const { unassignedDevices, areUnassignedDevicesLoaded } = useStoreContext()
  const [showArchiveDeviceModal, setShowArchiveDeviceModal] = useState<ArchiveDeviceModalState>({})

  if (!areUnassignedDevicesLoaded) {
    return <Spinner variant="page" />
  }

  const onCreateCallback = async (archivedDevice: Omit<ArchivedDevice, 'id' | 'createdAt'>) => {
    await Api.ArchivedDevices.create(archivedDevice.dealerId, archivedDevice)
      .then(() => {
        refreshCallback()
      })
  }

  return (
    <SinglePanePage>
      <S.Top>
        <BreadCrumbLink toRoute={'/customers/'} label={'CUSTOMERS'} direction={'back'} />
      </S.Top>

      <S.Header>
        <S.PageTitle>{unassignedDevices.length} Unassigned Device{unassignedDevices.length !== 1 ? 's' : ''}</S.PageTitle>
      </S.Header>

      <Table
        items={unassignedDevices}
        noDataMessage={{
          title: 'No Unassigned devices',
          description: 'Devices that have been unassigned will show up here.'
        }}
      >
        <Table.Header>
          <Table.HeaderCell title="AUID" />
          <Table.HeaderCell title="Serial Number" />
          <Table.HeaderCell title="Model" />
          <Table.HeaderCell title="Status" />
          <Table.HeaderCell title="" />
        </Table.Header>

        {unassignedDevices.map(unassignedDevice => (
          <div key={unassignedDevice.deviceId}>
            <Table.Row>
              <S.Cell>
                <NavLink to={`/unassigned/${unassignedDevice.deviceId}`}>
                  {unassignedDevice.deviceId}
                </NavLink>
              </S.Cell>
              <S.Cell>{unassignedDevice.serialNumber}</S.Cell>
              <S.Cell>{unassignedDevice.displayModel}</S.Cell>
              <S.Cell>{unassignedDevice.status}</S.Cell>
              <S.Cell>
                <Button decoration="danger-secondary" text="ARCHIVE SYSTEM" onPress={() => setShowArchiveDeviceModal(showArchiveDeviceModal => ({ ...showArchiveDeviceModal, [unassignedDevice.deviceId]: true }))} />
              </S.Cell>
            </Table.Row>

            <ArchiveDeviceModal
              isVisible={showArchiveDeviceModal[unassignedDevice.deviceId]}
              auid={unassignedDevice.deviceId}
              onUpdateCallback={(reason) => onCreateCallback({ auid: unassignedDevice.deviceId, dealerId, registered: unassignedDevice.registered, reason })}
              closeCallback={() => setShowArchiveDeviceModal(showArchiveDeviceModal => ({ ...showArchiveDeviceModal, [unassignedDevice.deviceId]: false }))}
              actionType="create"
            />
          </div>
        ))}
      </Table>
    </SinglePanePage>
  )
}

const CellStyles = `
  display: flex;
  line-height: 80px;
  padding-left: 10px;
  text-overflow: ellipsis;
  overflow: hidden;

  @media ${screenSize.medium} {
    line-height: 30px;
    margin: 15px 0px;
  }
  @media ${screenSize.small} {
    margin: 5px 0px;
  }
`

const S = {
  PageTitle: styled(PageTitle)`
    margin-top: revert;

    @media ${screenSize.small} {
      display: block;
    }
  `,
  Cell: styled(Table.Cell)`
    ${CellStyles}
  `,
  Header: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  Top: styled.div`
    display: flex;
    justify-content: flex-start;
  `
}

export default UnassignedDevices
